 import React, { useMemo, useState } from 'react';
import {
  X,
  Download,
  Filter,
  LayoutList,
  AlertCircle,
  CheckCircle2,
  Lightbulb,
  BarChart3,
  ShieldAlert,
  Target,
  Database,
  Rocket,
} from 'lucide-react';
import { PostIt, PostItType, CardData, ActionPlan } from '../types';
import { KaizenImpactModal, KaizenImpactData } from './KaizenImpactModal';
import { useProjectStore } from '../store/useProjectStore';

type KaizenFilter = 'all' | PostItType;

interface KaizenLogPanelProps {
  postIts: PostIt[];
  cards: CardData[];
  selectedCardId: string | null;
  selectedPostItId: string | null;
  filter: KaizenFilter;
  onChangeFilter: (f: KaizenFilter) => void;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  onSelectPostIt: (id: string) => void;
  onReorderPostIts: (updated: PostIt[]) => void;
   onClearCardFilter: () => void;
  onUpdatePostIt?: (postItId: string, updates: Partial<PostIt>) => void;
  onGenerateFutureMap?: () => void;
}

const typeLabels: Record<PostItType, string> = {
  idea: 'Ideias',
  problem: 'Problemas',
  action: 'Ações',
  data: 'Dados',
  risk: 'Riscos',
};

const typeIcons: Record<PostItType, string> = {
  problem: 'â—',
  action: 'âœ”',
  idea: 'ðŸ’¡',
  data: 'ðŸ“Š',
  risk: 'ðŸ›¡ï¸',
};

export const KaizenLogPanel: React.FC<KaizenLogPanelProps & { onClose: () => void }> = ({
  postIts,
  cards,
  selectedCardId,
  selectedPostItId,
  filter,
  onChangeFilter,
  isCollapsed,
  onToggleCollapsed,
  onSelectPostIt,
  onReorderPostIts,
  onClearCardFilter,
  onUpdatePostIt,
  onGenerateFutureMap,
  onClose,
}) => {
  const [actionPlanModalOpen, setActionPlanModalOpen] = useState(false);
  const [selectedActionPostIt, setSelectedActionPostIt] = useState<PostIt | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  
  // Verificar se já tem mapa futuro gerado
  const hasFutureMap = useProjectStore(state => state.hasFutureMap);
  const stage = useProjectStore(state => state.stage);
  
  // Contar post-its com impactos cadastrados
  const postItsWithImpacts = useMemo(() => 
    postIts.filter(p => p.impactos && p.impactos.length > 0).length,
    [postIts]
  );

  const countsByType = useMemo(() => {
    const base: Record<KaizenFilter, number> = {
      all: postIts.length,
      problem: 0,
      action: 0,
      idea: 0,
      data: 0,
      risk: 0,
    };
    for (const p of postIts) {
      base[p.type] += 1;
    }
    return base;
  }, [postIts]);

  const visiblePostIts = useMemo(() => {
    let list = [...postIts];

    if (selectedCardId) {
      list = list.filter((p) => p.fixedToNodeId === selectedCardId);
    }

    if (filter !== 'all') {
      list = list.filter((p) => p.type === filter);
    }

    list.sort((a, b) => {
      const ao = a.order ?? 0;
      const bo = b.order ?? 0;
      if (ao !== bo) return ao - bo;
      return 0;
    });

    return list;
  }, [postIts, selectedCardId, filter]);

  const groupedByCard = useMemo(() => {
    const map = new Map<string, PostIt[]>();
    visiblePostIts.forEach((p) => {
      const key = p.fixedToNodeId ?? '__sem_card__';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    });
    return map;
  }, [visiblePostIts]);

  const getCardLabel = (cardId: string | null) => {
    if (!cardId) return 'Sem vínculo';
    const card = cards.find((c) => c.id === cardId);
    if (!card) return 'Card removido';
    if (card.tipo === 'card_inicial') return card.nome_fluxo || 'Início';
    return card.atividade || 'Atividade';
  };

  const handleActionPostItClick = (postIt: PostIt) => {
    if (postIt.type === 'action') {
      setSelectedActionPostIt(postIt);
      setActionPlanModalOpen(true);
    }
    onSelectPostIt(postIt.id);
  };

  const handleSaveActionPlan = (data: KaizenImpactData) => {
    if (selectedActionPostIt && onUpdatePostIt) {
      onUpdatePostIt(selectedActionPostIt.id, { 
        text: data.what,
        impactos: data.impactos || [],
      });
    }
    setActionPlanModalOpen(false);
    setSelectedActionPostIt(null);
  };

  const handleExportActionPlan = () => {
    const actionPostIts = postIts.filter((p) => p.type === 'action' && p.actionPlan);
    
    if (actionPostIts.length === 0) {
      alert('Nenhuma ação com plano cadastrado para exportar.');
      return;
    }

    // Criar conteúdo do arquivo
    let content = 'PLANO DE AÇÃO - EXPORTAÇÃO\n';
    content += '='.repeat(50) + '\n\n';
    
    actionPostIts.forEach((postIt, index) => {
      const cardLabel = getCardLabel(postIt.fixedToNodeId);
      content += `AÇÃO ${index + 1}\n`;
      content += `-`.repeat(30) + '\n';
      content += `Descrição: ${postIt.text || '(sem descrição)'}\n`;
      content += `Vinculado a: ${cardLabel}\n`;
      
      if (postIt.actionPlan) {
        content += `\nPLANO DE AÇÃO:\n`;
        content += `  O Que: ${postIt.actionPlan.oQue}\n`;
        content += `  Onde: ${postIt.actionPlan.onde}\n`;
        content += `  Quem: ${postIt.actionPlan.quem}\n`;
        content += `  Quando: ${postIt.actionPlan.quando}\n`;
      }
      
      content += '\n' + '='.repeat(50) + '\n\n';
    });

    // Criar e baixar arquivo
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `plano-de-acao-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDragStart = (id: string) => {
    setDraggingId(id);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (targetId: string) => {
    if (!draggingId || draggingId === targetId) return;

    const updated = [...postIts];
    const draggingIndex = updated.findIndex((p) => p.id === draggingId);
    const targetIndex = updated.findIndex((p) => p.id === targetId);
    if (draggingIndex === -1 || targetIndex === -1) return;

    const [dragItem] = updated.splice(draggingIndex, 1);
    updated.splice(targetIndex, 0, dragItem);

    updated.forEach((p, index) => {
      (p as any).order = index;
    });

    onReorderPostIts(updated);
    setDraggingId(null);
  };

  const renderPostItRow = (p: PostIt) => {
    const isActive = p.id === selectedPostItId;
    const isDragging = p.id === draggingId;
    
    // Buscar card alvo para simulação
    const targetCard = p.targetNodeId ? cards.find(c => c.id === p.targetNodeId) : null;

    return (
      <div
        key={p.id}
        className={`w-full text-left border rounded px-2 py-1.5 text-[11px] transition flex flex-col gap-0.5 cursor-pointer ${
          isActive ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
        } ${isDragging ? 'opacity-60 scale-[0.98]' : ''}`}
        draggable
        onDragStart={() => handleDragStart(p.id)}
        onDragOver={handleDragOver}
        onDrop={() => handleDrop(p.id)}
        onClick={() => handleActionPostItClick(p)}
      >
        <div className="flex items-center gap-2 mb-0.5">
          <span className="inline-flex items-center gap-1 px-2 py-[2px] rounded-sm bg-slate-200 text-[10px] font-semibold text-slate-800">
            <span>{typeIcons[p.type]}</span>
            <span>{typeLabels[p.type]}</span>
          </span>
        </div>
        {isCollapsed ? (
          <span className="text-[11px] text-slate-800 truncate">
            {p.text || '(sem texto)'}
          </span>
        ) : (
          <>
            <span className="text-[11px] text-slate-800">
              {p.text || '(sem texto)'}
            </span>
            <span className="text-[10px] text-slate-500 flex items-center gap-1">
              {getCardLabel(p.fixedToNodeId)}
            </span>
            
            {/* Informações de Simulação (Post-it de Ação) */}
            {p.type === 'action' && targetCard && (
              <div className="mt-1 pt-1 border-t border-slate-200">
                <div className="text-[10px] font-semibold text-green-700 flex items-center gap-1">
                  <span>ðŸŽ¯</span>
                  <span>Alvo: {targetCard.atividade || targetCard.nome_fluxo || 'Card'}</span>
                </div>
                {p.improvementType && (
                  <div className="text-[9px] text-slate-600 mt-0.5">
                    {p.improvementType === 'tempo_processamento' ? 'â±ï¸ Redução de TRA' : 'â¸ï¸ Eliminação de TP'}
                  </div>
                )}
                {p.estimatedGain !== undefined && (
                  <div className="text-[9px] text-green-600 font-medium">
                    ðŸ“ˆ Ganho: {p.estimatedGain}%
                  </div>
                )}
                {p.investment !== undefined && p.investment > 0 && (
                  <div className="text-[9px] text-amber-600">
                    ðŸ’° Investimento: R$ {p.investment.toLocaleString('pt-BR')}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        {p.type === 'action' && p.actionPlan && (
          <span className="text-[10px] text-green-600 font-medium mt-0.5">
            âœ“ Plano configurado
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="w-full h-full bg-white flex flex-col shrink-0 box-border overflow-x-hidden">
        {/* Header */}
        <div className="h-12 px-3 border-b border-slate-200 flex items-center justify-between bg-slate-50">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wide text-slate-700">
              Kaizen Log
            </div>
            <div className="text-[10px] text-slate-500">
              {selectedCardId ? 'Post-its do card selecionado' : 'Post-its de todos os cards'}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={onToggleCollapsed}
              className="px-2 py-1 rounded-sm border border-slate-300 bg-white text-[10px] text-slate-600 hover:bg-slate-100"
            >
              {isCollapsed ? 'Detalhado' : 'Compacto'}
            </button>
            <button
              onClick={onClose}
              className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              title="Fechar Kaizen Log"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Banner de contexto quando filtrando por card */}
        {selectedCardId && (
          <div className="px-3 py-1 border-b border-slate-200 bg-blue-50 flex items-center justify-between text-[10px] text-blue-700">
            <div className="flex items-center gap-1">
              <Filter size={11} className="text-blue-600" />
              <span>Filtrando por card selecionado</span>
            </div>
            <button
              onClick={onClearCardFilter}
              className="text-[10px] font-semibold underline hover:text-blue-900"
            >
              Ver todos
            </button>
          </div>
        )}

        {/* Filtros (stat cards em grid) */}
        <div className="px-2 py-2 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-1 mb-2">
            <Filter size={12} className="text-slate-500" />
            <span className="text-[10px] font-semibold text-slate-600 uppercase">Resumo Kaizen</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {(['all', 'problem', 'action', 'idea', 'data', 'risk'] as KaizenFilter[]).map((key) => {
              const label =
                key === 'all'
                  ? 'Todos'
                  : typeLabels[key as PostItType];
              const count = countsByType[key];
              const isActive = filter === key;

              let activeClasses = '';
              let iconColor = 'text-slate-600';
              let iconBg = 'bg-slate-100';
              switch (key) {
                case 'problem':
                  activeClasses = 'bg-rose-50 border-rose-300';
                  iconBg = 'bg-rose-100';
                  iconColor = 'text-rose-700';
                  break;
                case 'action':
                  activeClasses = 'bg-emerald-50 border-emerald-300';
                  iconBg = 'bg-emerald-100';
                  iconColor = 'text-emerald-700';
                  break;
                case 'idea':
                  activeClasses = 'bg-amber-50 border-amber-300';
                  iconBg = 'bg-amber-100';
                  iconColor = 'text-amber-700';
                  break;
                case 'data':
                  activeClasses = 'bg-sky-50 border-sky-300';
                  iconBg = 'bg-sky-100';
                  iconColor = 'text-sky-700';
                  break;
                case 'risk':
                  activeClasses = 'bg-violet-50 border-violet-300';
                  iconBg = 'bg-violet-100';
                  iconColor = 'text-violet-700';
                  break;
                case 'all':
                default:
                  activeClasses = 'bg-blue-50 border-blue-300';
                  iconBg = 'bg-blue-100';
                  iconColor = 'text-blue-700';
                  break;
              }

              const IconComp =
                key === 'all'
                  ? LayoutList
                  : key === 'problem'
                  ? AlertCircle
                  : key === 'action'
                  ? Target
                  : key === 'idea'
                  ? Lightbulb
                  : key === 'data'
                  ? Database
                  : ShieldAlert;

              return (
                <button
                  key={key}
                  onClick={() => onChangeFilter(key)}
                  className={`w-full flex items-center rounded border px-2 py-2 text-left transition hover:-translate-y-[1px] hover:shadow-sm ${
                    isActive
                      ? `${activeClasses} shadow-sm`
                      : 'bg-white border-slate-200'
                  }`}
                >
                  <div
                    className={`flex items-center justify-center w-7 h-7 rounded-full mr-2 ${isActive ? iconBg : 'bg-slate-100'}`}
                  >
                    <IconComp
                      size={16}
                      className={isActive ? iconColor : 'text-slate-500'}
                    />
                  </div>
                  <div className="flex flex-col items-start">
                    <span className="text-[14px] font-bold leading-tight text-slate-900">
                      {count}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.06em] text-slate-500">
                      {label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Lista de Post-its */}
        <div className="flex-1 overflow-y-auto p-2 space-y-3">
          {visiblePostIts.length === 0 && (
            <div className="text-[11px] text-slate-400 text-center mt-4 px-2">
              Nenhum post-it para os filtros atuais.
            </div>
          )}

          {selectedCardId ? (
            visiblePostIts.map((p) => renderPostItRow(p))
          ) : (
            Array.from(groupedByCard.entries()).map(([cardId, items]) => (
              <div key={cardId}>
                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-600 mb-1">
                  {getCardLabel(cardId === '__sem_card__' ? null : cardId)}
                </div>
                <div className="space-y-1.5">
                  {items.map((p) => renderPostItRow(p))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer: botões de ação */}
        <div className="mt-auto px-2 pt-2 pb-2 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 space-y-2">
          {/* Botão Gerar Mapa Futuro - aparece quando há impactos e ainda não gerou mapa futuro */}
          {!hasFutureMap && postItsWithImpacts > 0 && stage === 'kaizen' && onGenerateFutureMap && (
            <button
              onClick={onGenerateFutureMap}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-bold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-sm shadow-sm hover:from-emerald-600 hover:to-emerald-700 hover:shadow-md transition-all"
            >
              <Rocket size={14} />
              <span>Gerar Mapa Futuro ðŸŽ¯</span>
            </button>
          )}
          
          {/* Indicador de progresso quando ainda não tem impactos */}
          {!hasFutureMap && postItsWithImpacts === 0 && stage === 'kaizen' && (
            <div className="text-[10px] text-center text-slate-500 dark:text-slate-400 py-1">
              <Target size={12} className="inline mr-1" />
              Clique em "Impactos" nos Post-its para projetar ganhos
            </div>
          )}
          
          {/* Botão Exportar - sempre visível */}
          <button
            onClick={handleExportActionPlan}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-sm bg-transparent hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-800 dark:hover:text-slate-100 hover:border-slate-400 dark:hover:border-slate-500 transition"
          >
            <Download size={12} />
            <span>Exportar Plano de Ação</span>
          </button>
        </div>
      </div>

      {/* Modal de Impacto Kaizen */}
      <KaizenImpactModal
        isOpen={actionPlanModalOpen}
        onClose={() => {
          setActionPlanModalOpen(false);
          setSelectedActionPostIt(null);
        }}
        onSave={handleSaveActionPlan}
        initialWhat={selectedActionPostIt?.text || ''}
        initialImpactos={selectedActionPostIt?.impactos}
        linkedNodeId={selectedActionPostIt?.fixedToNodeId}
        linkedPostItId={selectedActionPostIt?.id}
      />
    </>
  );
};
